// Zentrale Re-Validation bei Tab-Refocus / Netz-zurueck.
//
// Problem: ein lange im Hintergrund liegender Tab zeigt veraltete Daten.
// Zwei Luecken greifen zusammen:
//   1. Polling-Stores laufen ueber setInterval -- Browser drosseln/
//      pausieren das im Hintergrund-Tab. Beim Zurueckkommen wartet man
//      bis zum naechsten Tick (bis zu 60s) auf frische Zahlen.
//   2. Die WS-Verbindung kann halb-offen sterben (Laptop-Sleep, Netz-
//      Wechsel): readyState bleibt OPEN, aber es kommen keine Frames
//      mehr und kein onclose feuert -> kein Reconnect. Ein Recovery-
//      Event, das z.B. die kritische Lane geleert haette, kommt nie an,
//      die Karte zeigt weiter den alten Fehler.
//
// Loesung: jeder Daten-Store registriert hier seine refresh-Funktion
// (im start*). Wird der Tab wieder sichtbar (nach nennenswerter Pause)
// oder kommt das Netz zurueck, kicken wir zuerst den WS-Hub (halb-tote
// Leitung neu aufbauen) und stossen dann alle registrierten Refreshes
// an. Das ergaenzt das SWR-Pattern aus data-loading.md um den Fall
// "Tab war Stunden offen".

import { revalidateWsHub } from "./ws-hub.svelte";

type Revalidator = () => void;

const _revalidators = new Set<Revalidator>();
let _installed = false;
let _hiddenAt = 0;

// Erst ab dieser Hintergrund-Dauer lohnt das volle Refetch-Gewitter.
// Kurzes Alt-Tab (< 15s) laesst die laufenden Polls/WS-Frames in Ruhe;
// der WS-Hub wird trotzdem bei jedem Sichtbar-Werden billig geprueft.
const MIN_HIDDEN_MS = 15_000;

/**
 * Store-refresh registrieren. Gibt einen Unregister-Callback zurueck.
 * Idempotent -- Set + stabile Funktions-Identitaet, doppeltes Anmelden
 * (z.B. wenn start* mehrfach aufgerufen wird) ist ein No-op.
 */
export function registerRevalidator(fn: Revalidator): () => void {
  _revalidators.add(fn);
  return () => _revalidators.delete(fn);
}

function revalidateStores(): void {
  for (const fn of _revalidators) {
    try {
      fn();
    } catch {
      // Ein Store darf die anderen nicht mitreissen.
    }
  }
}

/** Transport pruefen + alle Stores neu laden. */
export function revalidateAll(): void {
  revalidateWsHub();
  revalidateStores();
}

/**
 * Einmalig die visibilitychange/online-Listener installieren. Wird aus
 * App.svelte nach Auth-OK aufgerufen, zusammen mit den start*-Aufrufen.
 */
export function startRevalidationWatch(): void {
  if (_installed) return;
  _installed = true;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      _hiddenAt = Date.now();
      return;
    }
    // Sichtbar: WS immer billig verifizieren (reconnectet nur, wenn die
    // Leitung wirklich tot ist).
    revalidateWsHub();
    const hiddenMs = _hiddenAt ? Date.now() - _hiddenAt : 0;
    _hiddenAt = 0;
    if (hiddenMs >= MIN_HIDDEN_MS) revalidateStores();
  });

  // Netz zurueck -> immer voll revalidieren, egal ob der Tab sichtbar war.
  window.addEventListener("online", () => revalidateAll());

  // WS-Reconnect (der ws-hub feuert v2:ws-reconnect nur beim ERNEUTEN
  // Oeffnen, nicht beim ersten). Deckt die Luecke, die visibilitychange/
  // online offen lassen: ein WAN-Ausfall bei sichtbarem Tab + verbundenem
  // Client-WLAN. Nach dem Reconnect laden alle Stores frisch -- so
  // verschwindet z.B. die "Dashboard extern"-Karte, sobald das Backend
  // wieder up meldet, statt bis zum naechsten Poll-Tick haengenzubleiben.
  window.addEventListener("v2:ws-reconnect", () => revalidateStores());
}
